import { create } from "zustand"
import { UIState, Notification } from "../types"
import { STORAGE_KEYS } from "@utils/constants"

interface UIStore extends UIState {
  sidebarCollapsed: boolean
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  toggleSidebarCollapse: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleTheme: () => void
  setTheme: (theme: "light" | "dark") => void
  addNotification: (notification: Omit<Notification, "id" | "createdAt">) => void
  removeNotification: (id: string) => void
  clearNotifications: () => void
  markNotificationAsRead: (id: string) => void
  markAllNotificationsAsRead: () => void
  getUnreadNotificationsCount: () => number
  setLoading: (loading: boolean) => void
}

export const useUIStore = create<UIStore>((set, get) => ({
  sidebarOpen: false,
  sidebarCollapsed: false,
  theme: (localStorage.getItem(STORAGE_KEYS.THEME) as "light" | "dark") || "light",
  loading: false,
  notifications: [],

  toggleSidebar: () => {
    set((state) => ({ sidebarOpen: !state.sidebarOpen }))
  },

  setSidebarOpen: (open: boolean) => {
    set({ sidebarOpen: open })
  },

  toggleSidebarCollapse: () => {
    set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }))
  },

  setSidebarCollapsed: (collapsed: boolean) => {
    set({ sidebarCollapsed: collapsed })
  },

  toggleTheme: () => {
    set((state) => ({
      theme: state.theme === "light" ? "dark" : "light",
    }))
  },

  setTheme: (theme: "light" | "dark") => {
    set({ theme })
  },

  addNotification: (notification: Omit<Notification, "id" | "createdAt">) => {
    const newNotification: Notification = {
      ...notification,
      id: Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString(),
    }

    set((state) => ({
      notifications: [...state.notifications, newNotification],
    }))

    if (notification.duration) {
      setTimeout(() => {
        get().removeNotification(newNotification.id)
      }, notification.duration)
    }
  },

  removeNotification: (id: string) => {
    set((state) => ({
      notifications: state.notifications.filter((notification) => notification.id !== id),
    }))
  },

  clearNotifications: () => {
    set({ notifications: [] })
  },

  markNotificationAsRead: (id: string) => {
    set((state) => ({
      notifications: state.notifications.map((notification) =>
        notification.id === id ? { ...notification, isRead: true } : notification,
      ),
    }))
  },

  markAllNotificationsAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((notification) => ({
        ...notification,
        isRead: true,
      })),
    }))
  },

  getUnreadNotificationsCount: () => {
    const state = get()
    return state.notifications.filter((notification) => !notification.isRead).length
  },

  setLoading: (loading: boolean) => {
    set({ loading })
  },
}))
